import { Head } from '@inertiajs/react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import SyntaxHighlighter from 'react-syntax-highlighter'
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs'
import { FileType, AlertCircle, CheckCircle, Shield, Zap } from "lucide-react"

export default function LimitFileTypes() {
  return (
    <div className="container mx-auto py-6 px-4 max-w-6xl">
      <Head title="Limit File Types - Documentation" />
      
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">File Type Validation</h1>
        <p className="text-lg text-gray-600 mb-4">
          The web component includes file type validation that allows you to restrict which file types users can upload using the standard HTML <code className="bg-gray-100 px-2 py-1 rounded">accept</code> attribute pattern.
        </p>
        
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <Shield className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>TLDR:</strong> Add an "accept" attribute to the html component with standard mime types (e.g. <code>image/jpeg</code>, <code>image/png</code>, <code>application/pdf</code>, <code>text/plain</code>)
              </p>
            </div>
          </div>
        </div>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>How It Works</CardTitle>
          <CardDescription>
            Understanding the file type validation process
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-600">
            The component validates files based on their MIME types using the <code className="bg-gray-100 px-2 py-1 rounded">accept</code> attribute. When a file is selected or dropped, the component checks if its MIME type matches the accepted types before proceeding with the upload.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                <div>
                  <h4 className="font-medium text-green-800 mb-1">MIME Type Validation</h4>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li>• Validates against the accept attribute</li>
                    <li>• Prevents upload of disallowed types</li>
                    <li>• Clear error messages for rejected files</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-start">
                <Zap className="w-5 h-5 text-purple-500 mr-2 mt-0.5" />
                <div>
                  <h4 className="font-medium text-purple-800 mb-1">Browser Integration</h4>
                  <ul className="text-sm text-purple-700 space-y-1">
                    <li>• Filters file picker dialog options</li>
                    <li>• Works with drag-and-drop</li>
                    <li>• Native browser experience</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Usage Examples</CardTitle>
          <CardDescription>
            Different ways to configure file type restrictions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">Basic Usage - Accept All Images</h3>
            <p className="text-gray-600 mb-3">
              Use wildcards to accept all files of a certain type:
            </p>
            <SyntaxHighlighter
              language="xml"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                fontSize: '.8rem',
                borderRadius: '6px'
              }}
            >
              {`<!-- Accept only image files -->
<simple-file-upload 
  accept="image/*"
  public-key="your-key"
></simple-file-upload>`}
            </SyntaxHighlighter>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">Specific MIME Types</h3>
            <p className="text-gray-600 mb-3">
              Restrict to specific file formats:
            </p>
            <SyntaxHighlighter
              language="xml"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                fontSize: '.8rem',
                borderRadius: '6px'
              }}
            >
              {`<!-- Accept only JPEGs and PDFs -->
<simple-file-upload 
  accept="image/jpeg,application/pdf"
  public-key="your-key"
></simple-file-upload>`}
            </SyntaxHighlighter>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">Multiple Wildcards</h3>
            <p className="text-gray-600 mb-3">
              Combine multiple wildcard patterns:
            </p>
            <SyntaxHighlighter
              language="xml"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                fontSize: '.8rem',
                borderRadius: '6px'
              }}
            >
              {`<!-- Accept all images and all text files -->
<simple-file-upload 
  accept="image/*,text/*"
  public-key="your-key"
></simple-file-upload>`}
            </SyntaxHighlighter>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">Mixed Patterns</h3>
            <p className="text-gray-600 mb-3">
              Combine wildcards with specific MIME types:
            </p>
            <SyntaxHighlighter
              language="xml"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                fontSize: '.8rem',
                borderRadius: '6px'
              }}
            >
              {`<!-- Accept all images, PDFs, and plain text -->
<simple-file-upload 
  accept="image/*,application/pdf,text/plain"
  public-key="your-key"
></simple-file-upload>`}
            </SyntaxHighlighter>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Common File Type Patterns</CardTitle>
          <CardDescription>
            Quick reference for frequently used file type restrictions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold mb-3 text-blue-600">Images</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm">All images</span>
                  <code className="text-xs bg-gray-200 px-2 py-1 rounded">image/*</code>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm">JPEG only</span>
                  <code className="text-xs bg-gray-200 px-2 py-1 rounded">image/jpeg</code>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm">PNG only</span>
                  <code className="text-xs bg-gray-200 px-2 py-1 rounded">image/png</code>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm">WebP only</span>
                  <code className="text-xs bg-gray-200 px-2 py-1 rounded">image/webp</code>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-3 text-green-600">Documents</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm">PDF</span>
                  <code className="text-xs bg-gray-200 px-2 py-1 rounded">application/pdf</code>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm">Word</span>
                  <code className="text-xs bg-gray-200 px-2 py-1 rounded">application/msword</code>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm">Excel</span>
                  <code className="text-xs bg-gray-200 px-2 py-1 rounded">application/vnd.ms-excel</code>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm">Plain text</span>
                  <code className="text-xs bg-gray-200 px-2 py-1 rounded">text/plain</code>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Combining with Other Validations</CardTitle>
          <CardDescription>
            File type validation works alongside other validation features
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-600">
            You can combine file type restrictions with other validation attributes for comprehensive upload control:
          </p>
          
          <SyntaxHighlighter
            language="xml"
            style={atomOneDark}
            customStyle={{
              background: '#21252b',
              padding: '1rem',
              margin: 0,
              fontSize: '.8rem',
              borderRadius: '6px'
            }}
          >
            {`<!-- Accept only images up to 1MB, maximum 5 files -->
<simple-file-upload 
  accept="image/*"
  max-file-size="1000000"
  max-files="5"
  public-key="your-key"
></simple-file-upload>`}
          </SyntaxHighlighter>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Error Handling</CardTitle>
          <CardDescription>
            How the component handles invalid file types
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5" />
              <div>
                <h4 className="font-medium text-red-800 mb-1">Error Message Format</h4>
                <p className="text-sm text-red-700">
                  When a file type is not accepted, users see: <code className="bg-red-100 px-1 py-0.5 rounded">File type 'mime/type' is not allowed</code>
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <h4 className="font-semibold">Key Features:</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">•</span>
                <span>Clear error messages are added to rejected files</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">•</span>
                <span>Valid files can still upload even when some files are rejected</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">•</span>
                <span>Validation happens before upload attempts, saving bandwidth</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">•</span>
                <span>Error messages combine with other validation errors</span>
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
